<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '@/composables/usePermissions';
import { usePopupManager } from '@/components/PopupManager.vue';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import updateUrl from '@/service/updateUrl';
import _ from '@/setup/lodash';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type { GetMediaBuyingUrlBlacklistsResponse } from '@/types/openapi-ts';
import type { GetMediaBuyingUrlBlacklistsData } from '@/types/openapi-ts/types.gen';
//#endregion

//#region types
type MediaBuyingUrlBlacklistsRow = GetMediaBuyingUrlBlacklistsResponse['payload'][number];
//#endregion

//#region const
const LS_PATH = 'MediaBuying/BlackListUrls';

const sizes = {
  1080: 20,
  1440: 30,
};

const fields = [
  {
    name: 'url',
    align: 'left',
    class: 'text',
    externalLink: (row: MediaBuyingUrlBlacklistsRow) => row.url,
    imgPrepend: (row: MediaBuyingUrlBlacklistsRow) => renderImg(row),
  },
  {
    name: 'created_at',
    width: '130',
    align: 'left',
    headerAlign: 'left',
    sortable: 'custom',
    format: 'date-time',
  },
] as UiTableNewField<MediaBuyingUrlBlacklistsRow>[];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const store = useTypedStore();
const popupManager = usePopupManager();
const { t } = useI18n();
const { hasPermission } = usePermissions();

const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});

const { sortProp, sortOrder, setSort, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'created_at',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});

const { searchQuery, setSearch } = useTableSearch();
//#endregion

//#region state
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const tableData = ref<MediaBuyingUrlBlacklistsRow[]>([]);
//#endregion

//#region computed
const postPlatform = computed(() => store.getters['misc/postPlatform']);
const isBlacklistEdit = computed(() => {
  return hasPermission('media_buying_payment_methods_blacklist_edit');
});
//#endregion

//#region methods
const _query = () => {
  return {
    ...optionsSort.value,
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
  } as GetMediaBuyingUrlBlacklistsData['query'];
};

const _completedFilterUrl = () => {
  const _dataFilters = {
    ..._.omit(_query(), ['offset']),
    page: page.value,
  };
  updateUrl.updateGetParams(_dataFilters);
};

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();

  try {
    const { data: { payload, misc } } = await api.getMediaBuyingBlacklistUrls(options);

    count.value = misc!.count;
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  if (!_.isEmpty(_urlData)) {
    setPage(_urlData);

    delete _urlData.page;
    delete _urlData.limit;
    _.merge(optionsSort.value, _urlData);

    setSort(_urlData);
    setSearch(_urlData);
  }

  nextTick(() => {
    fetchData();
  });
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

function handleOpenBlacklistUrlPopup(row?: MediaBuyingUrlBlacklistsRow) {
  const data = row
    ? {
      id: row.id,
      url: row.url,
    }
    : undefined;

  popupManager.blacklistUrlPopup.open({
    data,
    listeners:{
      success() {
        fetchData();
      },
    },
  });
}

function renderImg(row: MediaBuyingUrlBlacklistsRow) {
  return (postPlatform.value.find((el) => el.id === row.url_platform_id))?.logo_base64;
}

function removeMethod(row: MediaBuyingUrlBlacklistsRow) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.blacklistUrls.popup.delete.msg', { address: row.url }),
      title: t('mediaBuying.blacklistUrls.popup.delete.title'),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('mediaBuying.blacklistUrls.popup.delete.action'),
      confirmType: 'negative',
      confirmIcon: 'trash-alt',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        await api.deleteMediaBuyingBlacklistUrls(row.id);
        await fetchData(true);
        context.handleClose();
      },
    },
  });
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="media-buying-black-list-url">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('mediaBuying.blacklistUrls.title') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="isBlacklistEdit"
          filled
          icon="plus"
          @click="handleOpenBlacklistUrlPopup"
        >
          {{ $t('mediaBuying.blacklistUrls.add') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="mediaBuying.blacklistUrls"
      @sort="handleSort"
    >
      <template
        v-if="isBlacklistEdit"
        v-slot:append
      >
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div
              :key="scope.$index"
              class="table-action-header"
            >
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="pen"
              :tooltip="$t('crm.buttons.edit')"
              @click="handleOpenBlacklistUrlPopup(scope.row)"
            />
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('crm.buttons.delete')"
              @click="removeMethod(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>

<style lang="scss">
.media-buying-black-list-url {
  .ui-table__table-img {
    padding: 0;
    height: 14px;
    margin-right: 4px;
  }
}
</style>
